import { useState, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Check, CaretUpDown, MagnifyingGlass } from '@phosphor-icons/react';

/**
 * A searchable single-select dropdown. Drop-in replacement for long filter dropdowns
 * (e.g. clinic / location lists) so users can type to find an option. (#13)
 * options: [{ value, label }]. Pass an "All" option as the first entry if needed.
 */
export default function SearchableSelect({
  value,
  options = [],
  onChange,
  placeholder = 'Select...',
  testid,
  className = '',
  buttonClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = options.find(o => String(o.value) === String(value));
  const term = q.trim().toLowerCase();
  const filtered = useMemo(
    () => (term ? options.filter(o => `${o.label || ''} ${o.keywords || ''}`.toLowerCase().includes(term)) : options),
    [term, options],
  );

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center justify-between gap-2 h-10 rounded-md border border-[#E2E8F0] bg-white px-3 text-sm text-left hover:border-[#0F4C81] transition-colors ${buttonClassName}`}
          data-testid={testid}
        >
          <span className={selected ? 'text-[#0F172A] truncate' : 'text-[#9CA3AF] truncate'}>
            {selected ? selected.label : placeholder}
          </span>
          <CaretUpDown size={14} className="shrink-0 text-[#64748B]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={`p-0 w-[var(--radix-popover-trigger-width)] min-w-[220px] ${className}`}
        align="start"
        data-testid={testid ? `${testid}-content` : undefined}
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <MagnifyingGlass size={14} className="text-[#64748B]" />
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Type to search..."
            className="w-full text-sm outline-none bg-transparent"
            data-testid={testid ? `${testid}-search` : undefined}
          />
        </div>
        <div onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; }} className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-[#9CA3AF]">No match</div>}
          {filtered.map(o => (
            <button
              key={o.value === '' ? '__all' : o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); setQ(''); }}
              className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left hover:bg-[#F1F5F9] ${String(o.value) === String(value) ? 'bg-[#EBF3FA] text-[#0F4C81]' : 'text-[#0F172A]'}`}
              data-testid={testid ? `${testid}-opt-${o.value === '' ? 'all' : o.value}` : undefined}
            >
              <span className="truncate">{o.label}</span>
              {String(o.value) === String(value) && <Check size={14} className="shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
